<script>
  // The expanded detail for a single capture: request line, metadata,
  // headers, query params and a syntax-highlighted body. Extracted from
  // CaptureRow so the Captures table and the per-bucket view (/buckets/:id,
  // via CaptureRow) render the exact same panel.
  import hljs from 'highlight.js/lib/core';
  import json from 'highlight.js/lib/languages/json';
  import {
    decodedBody, prettyBody, isJsonBody, reconstructUrl, parsedQuery, requestLine,
  } from '../lib/capture.js';

  // JSON-only hljs keeps the bundle small; token classes are themed in app.css.
  hljs.registerLanguage('json', json);

  let { capture } = $props();

  function fmt(t) { return new Date(t).toLocaleString(); }
  function highlightedBody(c) {
    return hljs.highlight(prettyBody(c), { language: 'json' }).value;
  }
</script>

<div class="cap-detail">
  <section class="cap-section">
    <h4>Request</h4>
    <pre class="cap-reqline">{requestLine(capture)}</pre>
    <dl class="cap-meta">
      <div><dt>Full URL</dt><dd><a href={reconstructUrl(capture)} target="_blank" rel="noopener" class="cap-url">{reconstructUrl(capture)}</a></dd></div>
      <div><dt>Method</dt><dd>{capture.method}</dd></div>
      <div><dt>HTTP</dt><dd>{capture.proto || '—'}</dd></div>
      <div><dt>Remote</dt><dd>{capture.remote_addr || '—'}</dd></div>
      <div><dt>Received</dt><dd>{fmt(capture.created_at)}</dd></div>
      <div><dt>Body size</dt><dd>{capture.body_size} bytes{capture.body_truncated ? ' (truncated)' : ''}</dd></div>
      <div><dt>Content-type</dt><dd>{capture.content_type || '—'}</dd></div>
      <div><dt>User-agent</dt><dd>{capture.user_agent || '—'}</dd></div>
      {#if capture.subject}<div><dt>Subject</dt><dd>{capture.subject}</dd></div>{/if}
      <div><dt>Bucket</dt><dd>{capture.bucket_id || 'global'}</dd></div>
    </dl>
  </section>

  <section class="cap-section">
    <h4>Headers</h4>
    {#if capture.headers && Object.keys(capture.headers).length > 0}
      <ul class="cap-kv-list">
        {#each Object.entries(capture.headers) as [k, vs]}
          <li class="cap-kv-card">
            <span class="cap-kv-key">{k}</span>
            <span class="cap-kv-val">{vs.join(', ') || '(empty)'}</span>
          </li>
        {/each}
      </ul>
    {:else}
      <p class="text-xs text-on-surface-variant">No headers.</p>
    {/if}
  </section>

  {#if parsedQuery(capture).length > 0}
    <section class="cap-section">
      <h4>Query params</h4>
      <!-- URLSearchParams iteration yields percent-decoded values, so
           `q=hello%20world` shows up as a literal space here. -->
      <ul class="cap-kv-list">
        {#each parsedQuery(capture) as [k, v]}
          <li class="cap-kv-card">
            <span class="cap-kv-key">{k}</span>
            <span class="cap-kv-val">{v || '(empty)'}</span>
          </li>
        {/each}
      </ul>
    </section>
  {/if}

  <section class="cap-section">
    <h4>Body{capture.content_type ? ` (${capture.content_type})` : ''}</h4>
    {#if capture.body}
      {#if isJsonBody(capture)}
        <pre class="cap-body-json hljs"><code class="language-json">{@html highlightedBody(capture)}</code></pre>
      {:else}
        <pre class="cap-body">{prettyBody(capture)}</pre>
      {/if}
    {:else}
      <p class="text-xs text-on-surface-variant">Empty body.</p>
    {/if}
  </section>
</div>

<style>
  .cap-detail {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
    padding: 0.875rem;
  }
  .cap-section h4 {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgb(var(--md-sys-color-on-surface-variant));
    margin: 0 0 0.375rem;
  }
  .cap-body {
    margin: 0;
    padding: 0.625rem 0.75rem;
    background: rgb(var(--md-sys-color-surface));
    border-radius: 8px;
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 12px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-all;
    max-block-size: 480px;
    overflow: auto;
  }
  /* JSON variant lets the global .hljs rule provide the editor-look background
     and padding; we just constrain the size to keep tall payloads scrollable. */
  .cap-body-json {
    margin: 0;
    font-size: 12px;
    line-height: 1.5;
    max-block-size: 480px;
    overflow: auto;
  }
  /* HTTP request line, e.g. "GET /api/foo?bar=baz HTTP/2.0" -- monospaced,
     wraps so the full URL is always readable even when long. */
  .cap-reqline {
    margin: 0 0 0.5rem;
    padding: 0.5rem 0.625rem;
    background: rgb(var(--md-sys-color-surface));
    border-radius: 6px;
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 12px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-all;
  }
  /* Two-column key/value list for the metadata block. Auto-fit so a wide
     viewport fits more columns; narrow screens collapse to one column. */
  .cap-meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 0.25rem 1rem;
    margin: 0;
    font-size: 12px;
  }
  .cap-meta div {
    display: grid;
    grid-template-columns: 110px 1fr;
    gap: 0.5rem;
    align-items: baseline;
  }
  .cap-meta dt { color: rgb(var(--md-sys-color-on-surface-variant)); }
  .cap-meta dd { margin: 0; word-break: break-all; }
  .cap-url {
    color: rgb(var(--md-sys-color-primary));
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 2px;
  }
  .cap-url:hover { text-decoration-style: solid; }

  /* Shared key/value card list: used by both Headers and Query params. Each
     name/value pair is a self-contained chip so a long value can wrap without
     dragging neighbours down. Auto-fit flows the cards into as many columns as
     the width allows. */
  .cap-kv-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.5rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .cap-kv-card {
    display: grid;
    gap: 0.125rem;
    padding: 0.5rem 0.625rem;
    background: rgb(var(--md-sys-color-surface));
    border-radius: 8px;
    border: 1px solid rgb(var(--md-sys-color-outline-variant) / 0.6);
    min-inline-size: 0;
  }
  .cap-kv-key {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgb(var(--md-sys-color-primary));
    word-break: break-all;
  }
  .cap-kv-val {
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 13px;
    color: rgb(var(--md-sys-color-on-surface));
    word-break: break-all;
    /* Preserve user-typed whitespace so a decoded "hello world" keeps its space. */
    white-space: pre-wrap;
  }
</style>
